import { flexRender } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";

import { FillArrowDownIcon } from "@/icon/shared";

import { DataGridProps } from "./DataGridProps";
import { LoadingView } from "./components/LoadingView";
import { Pagination } from "./components/Pagination";

/**
 * This aligns the text in the cell and header. Comes from columnDef.meta
 */
type Alignment = {
  align?: "left" | "right" | "center";
};

/**
 * This is a DataGrid receiving props from the React-Table library.
 */
export const DataGrid = <T,>({
  table,
  className,
  headClassName,
  bodyClassName,
  rowClassName,
  sortingIcon = <FillArrowDownIcon className="h-1.5 w-3 fill-slate-500" />,
  enablePagination,
  onRowClick,
  stickyColumn,
  isLoading,
  rowsPerPage = [10, 25, 50, 100],
  emptyView,
  maxTableHeight,
  lang,
  ...props
}: DataGridProps<T>) => (
  <div className="relative">
    {isLoading && <LoadingView />}
    {table.getRowModel().rows.length === 0 && !isLoading && (
      <div className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2">
        {emptyView ? emptyView : "No data"}
      </div>
    )}
    <div className="overflow-auto" style={{ height: maxTableHeight }}>
      <table
        {...props}
        className={twMerge(
          table.getRowModel().rows.length === 0 &&
            !isLoading &&
            "min-h-[350px]",
          className
        )}
      >
        <thead className="sticky top-0 after:absolute after:bottom-0 after:left-0 after:right-0 after:h-px after:bg-gray-200">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className={headClassName}>
              {headerGroup.headers.map((header) => {
                const alignment =
                  (header.column.columnDef.meta as Alignment)?.["align"] ??
                  "left";

                return (
                  <th
                    key={header.id}
                    colSpan={header.colSpan}
                    style={{
                      width: header.getSize(),
                    }}
                    className={twMerge(
                      "px-2",
                      stickyColumn &&
                        "first-of-type:sticky first-of-type:left-0"
                    )}
                  >
                    <div
                      className={twMerge(
                        header.column.getCanSort() &&
                          "group cursor-pointer select-none transition-all hover:opacity-75"
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div
                        className={twMerge(
                          "h-full w-full",
                          header.column.getCanSort() &&
                            "flex items-center gap-1",
                          alignment === "left" && "justify-start text-left",
                          alignment === "right" &&
                            "flex-row-reverse text-right",
                          alignment === "center" && "justify-center text-center"
                        )}
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {header.column.getCanSort() && (
                          <span
                            className={twMerge(
                              "group-hover:visible group-hover:opacity-40",
                              header.column.getIsSorted()
                                ? "visible"
                                : "invisible",
                              header.column.getIsSorted() === "asc" &&
                                "rotate-180"
                            )}
                          >
                            {sortingIcon}
                          </span>
                        )}
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className={bodyClassName}>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={rowClassName}
              onClick={() => {
                onRowClick?.(row.original);
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const alignment =
                  (cell.column.columnDef.meta as Alignment)?.["align"] ??
                  "left";

                return (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className={twMerge(
                      "px-2",
                      alignment === "left" && "text-left",
                      alignment === "right" && "text-right",
                      alignment === "center" && "text-center",
                      stickyColumn &&
                        "first-of-type:sticky first-of-type:left-0"
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {enablePagination && (
      <Pagination table={table} rowsPerPage={rowsPerPage} lang={lang} />
    )}
  </div>
);
